// ============================================================================
// Shopee — Products (item_list + item_base_info).
//
// Shopee splits product fetching into two calls:
//   1. `/api/v2/product/get_item_list` — paginated list of item_ids + basic
//      metadata (status, update_time). Offset-based pagination with
//      `has_next_page` + `next_offset`.
//   2. `/api/v2/product/get_item_base_info` — batch fetch of FULL item details
//      by item_id. MAX 50 item_ids per call (Shopee's hard cap).
//
// The capability layer `fetchProducts` glues these together: list → slice into
// batches of 50 → batch-fetch → map → return one page. Full-sync can happen
// in one capability call when the list page size matches the batch cap.
//
// Shopee stores prices in `price_info[]` with `current_price` (discounted) +
// `original_price`. We surface `current_price` as the canonical `price` —
// that's what the buyer actually pays, and it's what downstream inventory /
// sales systems need for invoice matching.
// ============================================================================

import type { CommerceProduct, CommerceVariant } from '../../capabilities/commerce'
import { shopeeApiCall } from './client'
import type {
  ShopeeItemBaseInfoResponse,
  ShopeeItemListResponse,
  ShopeeItemRaw,
  ShopeeModelRaw,
} from './types'
import { ShopeeError } from './types'

// ============================================================================
// Constants
// ============================================================================

/** Shopee's hard cap on item_ids per get_item_base_info call. */
export const SHOPEE_ITEM_BATCH_MAX = 50

/** Default page size for get_item_list. */
export const SHOPEE_LIST_PAGE_DEFAULT = 50

// ============================================================================
// Auth context used by every helper below
// ============================================================================

export interface ShopeeAuthContext {
  partnerId: string | number
  partnerKey: string
  accessToken: string
  shopId: string | number
  apiBase?: string
  fetchImpl?: typeof fetch
}

// ============================================================================
// listShopeeItemList — paginated list of item ids
// ============================================================================

export interface ListShopeeItemListInput extends ShopeeAuthContext {
  /** Offset — 0-based. Pass `next_offset` from the previous page to paginate. */
  offset?: number
  /** Page size. Shopee caps at 100; we default to `SHOPEE_LIST_PAGE_DEFAULT`. */
  pageSize?: number
  /** Filter by item status — `NORMAL`, `DELETED`, `BANNED`, or `UNLIST`. */
  itemStatus?: 'NORMAL' | 'DELETED' | 'BANNED' | 'UNLIST'
  /** Lower bound on `update_time` (epoch seconds). */
  updateTimeFrom?: number
  /** Upper bound on `update_time` (epoch seconds). */
  updateTimeTo?: number
}

export interface ListShopeeItemListOutput {
  itemIds: number[]
  hasNextPage: boolean
  nextOffset?: number
  totalCount?: number
}

/**
 * List item ids on the authorised shop with offset-based pagination.
 * The result carries only ids + update_time; call `getShopeeItemBaseInfo`
 * with the ids (chunked to 50) to pull full details.
 */
export async function listShopeeItemList(
  input: ListShopeeItemListInput,
): Promise<ListShopeeItemListOutput> {
  const offset = typeof input.offset === 'number' && input.offset >= 0 ? Math.floor(input.offset) : 0
  const pageSize =
    typeof input.pageSize === 'number' && input.pageSize > 0
      ? Math.min(100, Math.floor(input.pageSize))
      : SHOPEE_LIST_PAGE_DEFAULT

  const query: Record<string, string | number | undefined> = {
    offset,
    page_size: pageSize,
    // Default to NORMAL so deleted/banned items don't bloat sync unless the
    // caller explicitly asks. Matches Shopee's documented default behaviour.
    item_status: input.itemStatus ?? 'NORMAL',
  }
  if (typeof input.updateTimeFrom === 'number') {
    query.update_time_from = Math.floor(input.updateTimeFrom)
  }
  if (typeof input.updateTimeTo === 'number') {
    query.update_time_to = Math.floor(input.updateTimeTo)
  }

  const apiInput: Parameters<typeof shopeeApiCall<ShopeeItemListResponse>>[0] = {
    apiPath: '/api/v2/product/get_item_list',
    method: 'GET',
    partnerId: input.partnerId,
    partnerKey: input.partnerKey,
    accessToken: input.accessToken,
    shopId: input.shopId,
    query,
    errorHint: 'item',
    ...(input.apiBase ? { apiBase: input.apiBase } : {}),
    ...(input.fetchImpl ? { fetchImpl: input.fetchImpl } : {}),
  }
  const resp = await shopeeApiCall<ShopeeItemListResponse>(apiInput)

  const items = resp.response?.item ?? []
  return {
    itemIds: items.map((i) => i.item_id).filter((id): id is number => typeof id === 'number'),
    hasNextPage: Boolean(resp.response?.has_next_page),
    nextOffset:
      typeof resp.response?.next_offset === 'number' ? resp.response.next_offset : undefined,
    totalCount:
      typeof resp.response?.total_count === 'number' ? resp.response.total_count : undefined,
  }
}

// ============================================================================
// getShopeeItemBaseInfo — batched full-detail fetch
// ============================================================================

export interface GetShopeeItemBaseInfoInput extends ShopeeAuthContext {
  /** The item_ids to fetch. Auto-split into batches of ≤ 50. */
  itemIds: number[]
}

/**
 * Fetch full item details for an arbitrary list of item_ids. Internally
 * splits into batches of `SHOPEE_ITEM_BATCH_MAX` (50) since that's Shopee's
 * hard cap, and concatenates the results. Returns raw `ShopeeItemRaw[]` so
 * callers can map via `shopeeItemsToProducts`.
 *
 * **⚠️ SILENT AUTO-SPLIT (Issue #255 M1):** when `itemIds.length > 50` this
 * helper transparently splits into multiple sequential API calls. Every batch
 * consumes one Shopee quota unit, so a caller that passes 500 ids incurs 10
 * round-trips. Budget accordingly — if the caller needs concurrency or a
 * hard batch cap, slice upstream before invoking.
 */
export async function getShopeeItemBaseInfo(
  input: GetShopeeItemBaseInfoInput,
): Promise<ShopeeItemRaw[]> {
  if (!Array.isArray(input.itemIds)) {
    throw new ShopeeError('INVALID_INPUT', 'itemIds must be an array')
  }
  if (input.itemIds.length === 0) return []

  const deduped = Array.from(new Set(input.itemIds.filter((id) => typeof id === 'number')))
  const batches: number[][] = []
  for (let i = 0; i < deduped.length; i += SHOPEE_ITEM_BATCH_MAX) {
    batches.push(deduped.slice(i, i + SHOPEE_ITEM_BATCH_MAX))
  }

  const all: ShopeeItemRaw[] = []
  for (const batch of batches) {
    const apiInput: Parameters<typeof shopeeApiCall<ShopeeItemBaseInfoResponse>>[0] = {
      apiPath: '/api/v2/product/get_item_base_info',
      method: 'GET',
      partnerId: input.partnerId,
      partnerKey: input.partnerKey,
      accessToken: input.accessToken,
      shopId: input.shopId,
      query: {
        item_id_list: batch.join(','),
      },
      errorHint: 'item',
      ...(input.apiBase ? { apiBase: input.apiBase } : {}),
      ...(input.fetchImpl ? { fetchImpl: input.fetchImpl } : {}),
    }
    const resp = await shopeeApiCall<ShopeeItemBaseInfoResponse>(apiInput)
    const items = resp.response?.item_list ?? []
    all.push(...items)
  }
  return all
}

// ============================================================================
// shopeeItemsToProducts — raw Shopee shape → CommerceProduct[]
// ============================================================================

/**
 * Map raw Shopee items to the canonical `CommerceProduct` shape. Variants
 * (`models` when `has_model=true`) are mapped to `CommerceVariant[]`.
 *
 * Aggregate stock calculation: if the item has variants, the top-level
 * `stock` is the sum of variant stocks; otherwise it's pulled from
 * `stock_info_v2.summary_info.total_available_stock`.
 */
export function shopeeItemsToProducts(items: ShopeeItemRaw[]): CommerceProduct[] {
  if (!Array.isArray(items)) return []
  const out: CommerceProduct[] = []
  for (const item of items) {
    if (!item || typeof item.item_id !== 'number') continue
    out.push(shopeeItemToProduct(item))
  }
  return out
}

function shopeeItemToProduct(item: ShopeeItemRaw): CommerceProduct {
  const priceInfo = Array.isArray(item.price_info) ? item.price_info[0] : undefined
  const currency = typeof priceInfo?.currency === 'string' ? priceInfo.currency : 'THB'
  const price =
    typeof priceInfo?.current_price === 'number'
      ? priceInfo.current_price
      : typeof priceInfo?.original_price === 'number'
        ? priceInfo.original_price
        : 0

  const variants: CommerceVariant[] | undefined =
    item.has_model && Array.isArray(item.models) && item.models.length > 0
      ? item.models.map(shopeeModelToVariant)
      : undefined

  const stock = variants
    ? variants.reduce((acc, v) => acc + (typeof v.stock === 'number' ? v.stock : 0), 0)
    : typeof item.stock_info_v2?.summary_info?.total_available_stock === 'number'
      ? item.stock_info_v2.summary_info.total_available_stock
      : 0

  const imageUrls = Array.isArray(item.image?.image_url_list) ? item.image!.image_url_list! : []

  const status: 'active' | 'inactive' | 'deleted' = (() => {
    switch ((item.item_status || '').toUpperCase()) {
      case 'NORMAL':
        return 'active'
      case 'DELETED':
      case 'BANNED':
        return 'deleted'
      case 'UNLIST':
        return 'inactive'
      default:
        return 'active'
    }
  })()

  const product: CommerceProduct = {
    id: String(item.item_id),
    name: typeof item.item_name === 'string' ? item.item_name : '',
    price,
    currency,
    stock,
    imageUrls,
    status,
  }
  if (typeof item.item_sku === 'string' && item.item_sku.length > 0) product.sku = item.item_sku
  if (typeof item.description === 'string' && item.description.length > 0) {
    product.description = item.description
  }
  if (typeof item.category_id === 'number') {
    product.categoryId = String(item.category_id)
  }
  if (typeof item.create_time === 'number') {
    product.createdAt = new Date(item.create_time * 1000).toISOString()
  }
  if (typeof item.update_time === 'number') {
    product.updatedAt = new Date(item.update_time * 1000).toISOString()
  }
  if (variants) {
    product.variants = variants
  }
  return product
}

function shopeeModelToVariant(model: ShopeeModelRaw): CommerceVariant {
  const priceInfo = Array.isArray(model.price_info) ? model.price_info[0] : undefined
  const variant: CommerceVariant = {
    id: String(model.model_id),
    name: typeof model.model_name === 'string' ? model.model_name : '',
    stock:
      typeof model.stock_info_v2?.summary_info?.total_available_stock === 'number'
        ? model.stock_info_v2.summary_info.total_available_stock
        : 0,
  }
  if (typeof model.model_sku === 'string' && model.model_sku.length > 0) {
    variant.sku = model.model_sku
  }
  if (typeof priceInfo?.current_price === 'number') {
    variant.price = priceInfo.current_price
  } else if (typeof priceInfo?.original_price === 'number') {
    variant.price = priceInfo.original_price
  }
  if (typeof model.image_info?.image_url === 'string' && model.image_info.image_url.length > 0) {
    variant.imageUrl = model.image_info.image_url
  }
  return variant
}
